// Brief visual flash on a character range. Used to make agent edits
// legible — when the agent mutates the AST, the resulting change in
// the buffer is hard to spot otherwise. Dispatching `flashChangeEffect`
// with a {from, to} range paints the range yellow for ~1200ms and
// scrolls it into the viewport (nearest-edge) if off-screen.
//
// Self-clearing: each flash has its own auto-expire timer keyed off
// the effect's dispatch time, so overlapping flashes don't cancel
// each other.

import { StateEffect, StateField } from '@codemirror/state';
import { Decoration, EditorView, type DecorationSet } from '@codemirror/view';

const FLASH_MS = 1200;

type FlashPayload = { from: number; to: number; id: number };

export const flashChangeEffect = StateEffect.define<FlashPayload>();
const clearFlashEffect = StateEffect.define<number>();

const flashMark = Decoration.mark({
  class: 'cm-agent-flash',
  attributes: { 'data-agent-flash': 'true' },
});

const flashField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    let next = decorations.map(tr.changes);
    for (const effect of tr.effects) {
      if (effect.is(flashChangeEffect)) {
        const { from, to } = effect.value;
        // Guard against ranges outside the current doc (e.g. after an
        // intermediate edit shrank the buffer). Clamp to doc length.
        const docLen = tr.state.doc.length;
        const clampedFrom = Math.max(0, Math.min(from, docLen));
        const clampedTo = Math.max(clampedFrom, Math.min(to, docLen));
        if (clampedTo > clampedFrom) {
          next = next.update({
            add: [flashMark.range(clampedFrom, clampedTo)],
          });
        }
      } else if (effect.is(clearFlashEffect)) {
        // Drop any decoration whose range no longer matches a live
        // flash. Cheap implementation: clear all. With flashes lasting
        // 1200ms and being fire-per-edit, collisions are rare and the
        // visual correction from a cleared-early flash is invisible.
        next = Decoration.none;
      }
    }
    return next;
  },
  provide: (f) => EditorView.decorations.from(f),
});

/** Register on a CodeMirror editor to enable flashing. */
export const changeFlashExtension = [flashField];

let nextId = 1;

/** Dispatch a flash + scroll on the given view. Safe to call before
 *  the view has settled — if the range is past the end of the doc the
 *  update logic clamps it. */
export function flashViewRange(
  view: EditorView,
  from: number,
  to: number,
): void {
  const id = nextId++;
  const docLen = view.state.doc.length;
  const anchor = Math.max(0, Math.min(from, docLen));
  view.dispatch({
    effects: [
      flashChangeEffect.of({ from, to, id }),
      EditorView.scrollIntoView(anchor, { y: 'nearest' }),
    ],
  });
  // Auto-clear after the flash duration. Using setTimeout so we don't
  // need a separate ticking extension.
  setTimeout(() => {
    try {
      view.dispatch({ effects: clearFlashEffect.of(id) });
    } catch {
      // view destroyed before timeout — fine.
    }
  }, FLASH_MS);
}
